import React, { useEffect, useState } from 'react';
import { Palette, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';
import { useTheme } from '../../lib/theme-provider';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

/**
 * Header control that lists the tenant's available themes and applies the
 * chosen one immediately on click.
 *
 * Apply happens in two steps:
 *  1. setTheme(key) — instantly repaints the UI by writing the theme's CSS
 *     variables onto <html> (see theme-provider.jsx).
 *  2. PUT /api/tenant/branding — persists the selection so it survives reloads
 *     and is shared across the tenant (the DB is the source of truth).
 */
export default function ColorThemeMenu() {
  const { tenant, updateTenant } = useAuth();
  const { themeId, setTheme } = useTheme();
  const [themes, setThemes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get('/api/tenant/themes')
      .then((data) => { if (active) setThemes(data || []); })
      .catch(() => { /* non-blocking: header control stays usable even if list fails */ });
    return () => { active = false; };
  }, []);

  const handleSelect = async (key) => {
    if (key === themeId || saving) return;
    const previous = themeId;
    // Apply instantly for immediate visual feedback.
    setTheme(key);
    setSaving(true);
    try {
      await api.put('/api/tenant/branding', { theme_id: key });
      updateTenant({ ...tenant, theme_id: key });
    } catch (e) {
      // Roll back the live theme if persistence fails.
      setTheme(previous);
      toast.error(e?.message || 'Could not apply theme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" title="Change color theme" data-testid="color-theme-trigger">
          <Palette className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Color Theme</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {themes.map((t) => (
          <DropdownMenuItem
            key={t.theme_key}
            onClick={() => handleSelect(t.theme_key)}
            className="cursor-pointer py-2 gap-3"
            data-testid={`color-theme-option-${t.theme_key}`}
          >
            <span
              className="h-3.5 w-3.5 rounded-full border flex-shrink-0"
              style={{ backgroundColor: t.primary_color }}
            />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium leading-tight">{t.display_name}</span>
              {t.description && (
                <span className="text-xs text-muted-foreground truncate">{t.description}</span>
              )}
            </div>
            {themeId === t.theme_key && (
              <Check className="h-4 w-4 ml-auto text-primary flex-shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
